import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { SitemarkIcon } from './components/CustomIcons';


export default function Welcome({ onLogin, onRegister }) {
	

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden'
      }}
    >

      {/* Top bar - stays fixed */}

      <Box
        sx={{
          height: 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <SitemarkIcon />

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600
          }}
        >
          DocsAsUDo
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Button
          onClick={onLogin}
          sx={{
            mr: 1,
            textTransform: 'none'
          }}
        >
          Log in
        </Button>

        <Button
          variant="contained"
          onClick={onRegister}
          sx={{
            textTransform: 'none'
          }}
        >
          Join Early Access
        </Button>
      </Box>


      {/* Main content - only this area scrolls */}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            py: 6
          }}
        >

          <Box sx={{ width: '100%' }}>

            {/* Hero */}

            <Box
              sx={{
                maxWidth: 780,
                mx: 'auto',
                textAlign: 'center',
                mb: 6
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '2.2rem',
                    md: '3.4rem'
                  },
                  mb: 2
                }}
              >
                Turn procedures into processes
                people actually follow.
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  lineHeight: 1.6,
                  maxWidth: 700,
                  mx: 'auto'
                }}
              >
                Define how work should happen, guide people through
                each step and capture the information and evidence
                as the work is done.
              </Typography>


              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row'
                }}
                spacing={2}
                justifyContent="center"
                sx={{ mt: 4 }}
              >
                <Button
                  size="large"
                  variant="contained"
                  onClick={onRegister}
                  sx={{
                    textTransform: 'none',
                    px: 4
                  }}
                >
                  Join Early Access
                </Button>

                <Button
                  size="large"
                  variant="outlined"
                  onClick={onLogin}
                  sx={{
                    textTransform: 'none',
                    px: 4
                  }}
                >
                  Log in
                </Button>
              </Stack>


              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                Free to use during Early Access.
              </Typography>
            </Box>


            {/* What it does */}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, 1fr)'
                },
                gap: 3,
                mb: 5
              }}
            >

              <Feature
                icon={<AccountTreeOutlinedIcon />}
                title="Define the process"
              >
                Model the way work should be performed,
                including responsibilities, decisions and approvals.
              </Feature>

              <Feature
                icon={<CheckCircleOutlineIcon />}
                title="Execute the process"
              >
                Give each person the work they need to perform
                and guide it through the organisation.
              </Feature>

              <Feature
                icon={<DescriptionOutlinedIcon />}
                title="Create the record"
              >
                Capture forms, decisions and supporting information
                as part of doing the work — not afterwards.
              </Feature>

            </Box>


            {/* Early access / hosting */}

            <Paper
              variant="outlined"
              sx={{
                p: {
                  xs: 3,
                  md: 4
                }
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: '1fr 1fr'
                  },
                  gap: 5
                }}
              >

                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 1
                    }}
                  >
                    Early Access
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7
                    }}
                  >
                    We're inviting organisations to use DocsAsUDo
                    on real business processes and help shape its
                    development. Use of the platform is free during
                    the Early Access period.
                  </Typography>
                </Box>


                <Box>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <BusinessOutlinedIcon />

                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600 }}
                    >
                      Your data, your choice
                    </Typography>
                  </Stack>

                  <Typography
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7
                    }}
                  >
                    Use our hosted service or talk to us about
                    deploying DocsAsUDo within your organisation's
                    own infrastructure for greater control over
                    security, integration and data residency.
                  </Typography>
                </Box>

              </Box>
            </Paper>

          </Box>
        </Container>
      </Box>
    </Box>
  );
}


function Feature({ icon, title, children }) {

  return (
    <Box
      sx={{
        textAlign: 'center',
        px: 2
      }}
    >
      <Box
        sx={{
          mb: 1.5,
          '& svg': {
            fontSize: 36
          }
        }}
      >
        {icon}
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 1
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.7
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}